import {Snackbar} from '@material-ui/core';
import {Alert, AlertTitle} from '@material-ui/lab';
import MuiAlert from '@material-ui/lab/Alert';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import {observer} from 'mobx-react-lite';
import {SessionProvider} from 'next-auth/react';
import {useCallback, useEffect, useState} from 'react';
import {Redirect, Route, BrowserRouter as Router, Switch} from 'react-router-dom';
import AdvisorIIVUL from '../AdvisorIIVUL/AdvisorIIVUL';
import Agent from '../Agent/Agent';
import {EmailEditorSelection} from '../Agent/ContactForm/EmailEditor/EmailEditorSelection';
import {FormHistory} from '../Agent/ContactForm/FormHistory';
import EditXfers from '../Agent/EditXfers/EditXfers';
import {ToolsPanel} from '../Agent/Tools/Tools';
import Analytics from '../Analytics/Analytics';
import {AnalyticsPanel} from '../Analytics/DailyFormSubmission';
import {DataFeedPanel} from '../Analytics/DataFeeds';
import {WAPanel} from '../Analytics/WAPanel';
import Authorized from '../Auth/Authorized';
import {CMPanel} from '../CaseManager/CM';
import {CustomerPortalDataTable} from '../CaseManager/CustomerPortalDataTable';
import FormModal from '../CaseManager/FormModal';
import RootStore from '../Core/mobx/RootStore';
import Home from '../Home/Home';
import Live from '../LiveCall/Live';
import Settings from '../Settings/Settings';
import RouteChangeConfirmDialog, {RouteChangeConfirmDialogProps} from '../UI/RouteChangeConfirmDialog';
import UrlShortener from '../UrlShortener/UrlShortener';
import UserAdmin from '../UserAdmin/UserAdmin';
import {IS_PRODUCTION} from '../constants';
import ClientDataAnalytics from './../Analytics/ClientDataAnalytics';
import AppBar from './AppBar';
import GrowthbookProvider from './GrowthbookProvider';
import {RealtimeProvider} from './RealtimeProvider';
import RouterInterceptor from './RouterInterceptor';

const App = observer(() => {
    const uiStore = RootStore.uiStore;
    const hideSnack = useCallback(
        AwesomeDebouncePromise(async () => {
            uiStore.closeSnackbar();
        }, 3000),
        [],
    );
    const drawerClosedWidth = 111;
    const drawerOpenWidth = 320;
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    const [confirmDialogConfig, setConfirmDialogConfig] = useState<RouteChangeConfirmDialogProps>({
        message: 'Are you sure?',
    });

    const getUserConfirmation = (message: string, callback: (ok: boolean) => void) => {
        setConfirmDialogConfig({
            message,
            callback,
        });
    };

    useEffect(() => {
        RootStore.fetchAgents();
    }, []);

    return (
        <SessionProvider refetchInterval={100}>
            <Authorized>
                {!IS_PRODUCTION && (
                    <div
                        style={{
                            marginLeft: `${isDrawerOpen ? drawerOpenWidth : drawerClosedWidth}px`,
                        }}
                    >
                        <Alert severity="warning">
                            <AlertTitle>Warning</AlertTitle>
                            <strong>
                                {typeof window === 'undefined' ||
                                window.location.hostname === 'ap-preprod.beamplify.com' ? (
                                    <>
                                        This is a pre-production endpoint with the newest features. Only the designated
                                        testers should be using this endpoint. Please report any issues in{' '}
                                        <a href="https://beamplify.slack.com/archives/C05JG08L0CB">
                                            #ap-preprod-testing
                                        </a>{' '}
                                        channel.
                                    </>
                                ) : (
                                    <>
                                        This is a staging environment. Please use{' '}
                                        <a href="https://platform.beamplify.com">Production endpoint</a>
                                    </>
                                )}
                            </strong>
                        </Alert>
                    </div>
                )}

                <Router getUserConfirmation={getUserConfirmation}>
                    <RouterInterceptor>
                        <GrowthbookProvider>
                            <RealtimeProvider>
                                <EmailEditorSelection />
                                <RouteChangeConfirmDialog
                                    message={confirmDialogConfig.message}
                                    callback={confirmDialogConfig.callback}
                                />
                                <Snackbar
                                    open={!!uiStore.snackbar}
                                    autoHideDuration={uiStore.snackbar?.duration}
                                    onClose={hideSnack}
                                >
                                    {uiStore.snackbar ? (
                                        <div
                                            onClick={() => {
                                                uiStore.snackbar?.action?.onClick?.();
                                                hideSnack();
                                            }}
                                            style={{
                                                cursor: uiStore.snackbar?.action?.onClick ? 'pointer' : 'default',
                                            }}
                                        >
                                            <MuiAlert elevation={6} variant="filled" severity={uiStore.snackbar?.type}>
                                                {uiStore.snackbar?.message}
                                            </MuiAlert>
                                        </div>
                                    ) : undefined}
                                </Snackbar>
                                <AppBar drawerState={isDrawerOpen} onDrawerStateChanged={setIsDrawerOpen} />
                                <div style={{marginLeft: `${isDrawerOpen ? drawerOpenWidth : drawerClosedWidth}px`}}>
                                    <Switch>
                                        <Route exact path="/agent">
                                            <Agent />
                                        </Route>

                                        <Redirect exact from="/agent/newform" to="/agent/newform/0" />

                                        <Route
                                            exact
                                            path="/agent/newform/:tabIndex"
                                            render={(props) => (
                                                <FormModal
                                                    match={props.match}
                                                    history={props.history}
                                                    location={props.location}
                                                    store={RootStore.agentPortalStore}
                                                />
                                            )}
                                        />

                                        <Redirect
                                            exact
                                            from="/agent/form/:email/:formId"
                                            to="/agent/form/:email/:formId/0"
                                        />

                                        <Route
                                            exact
                                            path="/agent/form/:email/:formId/history"
                                            render={(props) => (
                                                <FormHistory
                                                    store={RootStore.agentPortalStore}
                                                    history={props.history}
                                                    match={props.match}
                                                    location={props.location}
                                                />
                                            )}
                                        />

                                        <Route
                                            exact
                                            path="/agent/form/:email/:formId/:tabIndex"
                                            render={(props) => (
                                                <FormModal
                                                    match={props.match}
                                                    history={props.history}
                                                    location={props.location}
                                                    store={RootStore.agentPortalStore}
                                                />
                                            )}
                                        />
                                        <Route
                                            exact
                                            path="/agent/form/:email/:formId/history/:historyFormId/:tabIndex"
                                            render={(props) => (
                                                <FormModal
                                                    match={props.match}
                                                    history={props.history}
                                                    location={props.location}
                                                    store={RootStore.agentPortalStore}
                                                />
                                            )}
                                        />
                                        <Redirect
                                            exact
                                            from="/case-manager/form/:email/:formId"
                                            to="/case-manager/form/:email/:formId/0"
                                        />
                                        <Route
                                            exact
                                            path="/case-manager/form/:email/:formId/:tabIndex"
                                            render={(props) => (
                                                <FormModal
                                                    match={props.match}
                                                    history={props.history}
                                                    location={props.location}
                                                    store={RootStore.agentPortalStore}
                                                />
                                            )}
                                        />
                                        <Route exact path="/case-manager/customer-portal-data/">
                                            <CustomerPortalDataTable />
                                        </Route>
                                        <Route exact path="/case-manager">
                                            <CMPanel />
                                        </Route>
                                        <Route exact path="/tools">
                                            <ToolsPanel parameterStore={null} />
                                        </Route>
                                        <Route exact path="/analytics">
                                            <Analytics />
                                        </Route>
                                        <Route exact path="/settings">
                                            <Settings />
                                        </Route>
                                        <Route exact path="/widget-analytics">
                                            <WAPanel />
                                        </Route>
                                        <Route exact path="/client-data-analytics">
                                            <ClientDataAnalytics />
                                        </Route>
                                        <Route exact path="/advisor-ii-vul">
                                            <AdvisorIIVUL />
                                        </Route>
                                        <Route exact path="/daily-form-submission">
                                            <AnalyticsPanel />
                                        </Route>
                                        <Route exact path="/data-feeds">
                                            <DataFeedPanel />
                                        </Route>
                                        <Route exact path="/edit/xfers">
                                            <EditXfers />
                                        </Route>
                                        <Route exact path="/">
                                            <Home />
                                        </Route>
                                        <Route exact path="/user-admin">
                                            <UserAdmin />
                                        </Route>
                                        <Route exact path="/url-shortener">
                                            <UrlShortener />
                                        </Route>
                                        <Route exact path="/live-call">
                                            <Live />
                                        </Route>
                                    </Switch>
                                </div>
                            </RealtimeProvider>
                        </GrowthbookProvider>
                    </RouterInterceptor>
                </Router>
            </Authorized>
        </SessionProvider>
    );
});

export default App;
